import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, computed, effect, input, viewChild } from '@angular/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';

import { injectSiteBudget } from '@models/sites/api/site-budget.api';
import { SiteModel } from '@models/sites/sites.store';

import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import {
  injectNgControl,
  NoopValueAccessorDirective,
} from '@shared/directives/noop-value-accessor.directive';
import { SkeletonLoaderDirective } from '@shared/directives/skeleton-loader.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

@Component({
  hostDirectives: [NoopValueAccessorDirective],
  imports: [
    AsyncPipe,
    DatePipe,
    TooltipDirective,
    SkeletonLoaderDirective,
    InputComponent,
    ReactiveFormsModule,
    IconComponent,
  ],
  selector: 'aux-effective-date-dropdown',
  standalone: true,
  templateUrl: './effective-date-dropdown.component.html',
})
export class EffectiveDateDropdownComponent {
  ngControl = injectNgControl();

  site = input.required<SiteModel | undefined>();

  selectedSiteId = computed(() => this.site()?.id);

  private siteBudgeOptionList = injectSiteBudget({ site_id: this.selectedSiteId });

  startingProtocolVersionId = computed(() => this.site()?.starting_protocol_version_id ?? '');

  selectedProtocolVersion = input.required<null | string>();
  selectedSiteBudgetVersion = input.required<null | string>();

  isEffectiveDateEditable = computed(() => {
    return (
      this.startingProtocolVersionId() !== this.selectedProtocolVersion() ||
      !this.siteBudgeOptionList.data()?.find((sbv) => sbv.id === this.selectedSiteBudgetVersion())
        ?.is_baseline
    );
  });
  siteActivationDate = computed(() => this.site()?.site_activation ?? '');
  validateEffectiveDates = computed(() => [
    (control: AbstractControl) => {
      const effectiveDate = dayjs(control.value as string);

      // Check that the selected effective date is not before the site activation date
      if (effectiveDate.isBefore(dayjs(this.siteActivationDate()))) {
        return { site_budget_effective_date_before_activation_date: true };
      }

      let hasDuplicateEffectiveDate = false;

      // Check that there is no site budget version on the same protocol version with the same effective date
      (this.siteBudgeOptionList.data() || [])
        .filter(
          (siteBudgetVersion) =>
            siteBudgetVersion.id !== this.selectedSiteBudgetVersion() &&
            siteBudgetVersion.patient_protocol_version_id === this.selectedProtocolVersion()
        )
        .map((siteBudgetVersion) => dayjs(siteBudgetVersion.effective_date))
        .forEach((date) => {
          hasDuplicateEffectiveDate ||= effectiveDate.isValid() && date.isSame(effectiveDate);
        });

      if (hasDuplicateEffectiveDate) {
        return { duplicate_site_budget_effective_date: true };
      }

      // Check that the selected effective date is not the site activation date for amendments
      const is_baseline = this.siteBudgeOptionList
        .data()
        ?.find((sbv) => sbv.id === this.selectedSiteBudgetVersion())?.is_baseline;

      if (effectiveDate.isSame(dayjs(this.siteActivationDate())) && !is_baseline) {
        return { site_budget_effective_date_on_activation_date: true };
      }

      return null;
    },
  ]);

  editMode = input(false);
  loading = input(false);

  effectiveDateInput = viewChild<InputComponent>('effectiveDateInput');

  _disableEffectiveDate = effect(() => {
    if (this.isEffectiveDateEditable()) {
      this.ngControl.control?.enable();
    } else {
      this.ngControl.control?.disable();
    }
  });
  effectiveDateTooltipText =
    'An effective date must be entered in order for the site budget amendment to be applied.';
  startingSiteBudgetVersionEffectiveDateText =
    'This site budget version uses the activation date from this site.';
}
